import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";

export const SMS_DONE_KEY = "sviin_sms_done";

export const SmsSignup = ({ source, dark = false, onDone }) => {
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const ink = dark ? "text-[#F4F2EE]" : "text-[#111111]";
  const muted = dark ? "text-[#F4F2EE]/65" : "text-[#111111]/65";
  const line = dark ? "border-[#F4F2EE]/40 focus-within:border-[#F4F2EE]" : "border-[#111111]/30 focus-within:border-[#111111]";

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { setError("Please tick the box to agree to texts."); return; }
    setState("loading");
    setError("");
    try {
      await api.post("/sms-subscribe", { phone, consent: true, source });
      setState("done");
      localStorage.setItem(SMS_DONE_KEY, "1");
      onDone?.();
    } catch (err) {
      setState("idle");
      const d = err?.response?.data?.detail;
      const status = err?.response?.status;
      setError(typeof d === "string" ? d : status === 422 ? "Enter a valid US mobile number." : "Couldn't sign you up right now, please try again.");
    }
  };

  if (state === "done")
    return (
      <p data-testid={`sms-${source}-success`} className={`label flex items-center gap-2 py-4 ${ink}`}>
        <Check size={14} className="text-[#E2541B]" /> You're in — your 10% code is on its way by text
      </p>
    );

  return (
    <form onSubmit={submit} data-testid={`sms-${source}-form`} className="max-w-xl">
      <div className={`flex items-end border-b ${line}`}>
        <label htmlFor={`sms-${source}`} className="sr-only">Mobile number</label>
        <input
          id={`sms-${source}`}
          data-testid={`sms-${source}-phone-input`}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          required
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Mobile number"
          className={`h-14 min-w-0 flex-1 bg-transparent text-lg outline-none ${ink} ${dark ? "placeholder:text-[#F4F2EE]/55" : "placeholder:text-[#111111]/45"}`}
        />
        <button data-testid={`sms-${source}-submit-button`} disabled={state === "loading"} className={`label group flex h-14 shrink-0 items-center gap-2 whitespace-nowrap transition-colors hover:text-[#E2541B] ${ink}`}>
          {state === "loading" ? "…" : "Text me"}
          <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
        </button>
      </div>
      <label className="mt-4 flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          data-testid={`sms-${source}-consent-checkbox`}
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className={`mt-0.5 h-4 w-4 shrink-0 appearance-none border ${dark ? "border-[#F4F2EE]/60 checked:border-[#F4F2EE] checked:bg-[#F4F2EE] checked:shadow-[inset_0_0_0_3px_#111111]" : "border-[#111111]/50 checked:border-[#111111] checked:bg-[#111111] checked:shadow-[inset_0_0_0_3px_#F4F2EE]"}`}
        />
        <span className={`text-xs leading-relaxed ${muted}`}>
          I agree to receive recurring automated marketing texts from Sviin at this number. Consent is not a condition of
          purchase. Msg &amp; data rates may apply. Msg frequency varies. Reply STOP to opt out, HELP for help.
        </span>
      </label>
      {error && <p role="alert" data-testid={`sms-${source}-error`} className="mt-3 text-xs text-[#E2541B]">{error}</p>}
    </form>
  );
};
